/**
 * Sprint BG Part 2 — Sentry stub (apps/admin)。
 *
 * apps/user 版と同設計。本 sprint では `@sentry/react` 未 install のため、
 * 実 init はされず silent no-op。dep 追加後に loadSentry の dynamic import が解決し有効化。
 *
 * apps/admin 特有事項:
 *   - admin は Email/Password で認証され uid が伴う。Sprint BG では sendDefaultPii は false 維持。
 *   - 将来的に `user: { id: uid, email: maskedEmail }` を Sentry.setUser するか docs/13 で要検討。
 */

import { type ReactNode } from 'react';

const DSN: string = import.meta.env.VITE_SENTRY_DSN ?? '';
const ENV: string = import.meta.env.MODE ?? 'development';

let initialized = false;

interface SentrySdkLike {
  init: (opts: Record<string, unknown>) => void;
  captureException: (err: unknown, ctx?: { extra?: Record<string, unknown> }) => void;
}

export async function initSentry(): Promise<void> {
  if (initialized) return;
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.info(
      '[Sentry] DSN 未設定のためスキップ (VITE_SENTRY_DSN を .env.local に設定すると有効化)。',
    );
    return;
  }
  const sentry = await loadSentry();
  if (!sentry) return;
  sentry.init({
    dsn: DSN,
    environment: ENV,
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });
  initialized = true;
  // eslint-disable-next-line no-console
  console.info('[Sentry] initialized', { environment: ENV });
}

async function loadSentry(): Promise<SentrySdkLike | null> {
  try {
    const moduleName = '@sentry/react';
    const mod = (await import(/* @vite-ignore */ moduleName)) as SentrySdkLike;
    return mod;
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] @sentry/react が install されていません (Sprint BG では未追加)。');
    return null;
  }
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  void loadSentry().then((sentry) => {
    if (!sentry) return;
    sentry.captureException(err, context ? { extra: context } : undefined);
  });
}

export function withSentryErrorBoundary(children: ReactNode, _fallback?: ReactNode): ReactNode {
  return children;
}
